package map.views

import auth.ApiUserStore
import auth.Features
import dev.fritz2.core.RenderContext
import dev.fritz2.core.RootStore
import dev.fritz2.core.invoke
import dev.fritz2.headless.foundation.utils.floatingui.utils.PlacementValues
import generated.OFFLINE_MODE
import koin.koinCtx
import kotlinx.coroutines.Job
import localization.TL
import localization.Translation
import model.isInternalAdminGroupAdminOrGroupOwner
import search.searchlayer.MapLayerMetadataListStore
import sharedlayers.sharedLayerPicker
import twcomponents.doIfFeatureFlagEnabled
import twcomponents.twColOf
import twcomponents.twColOfCenter
import twcomponents.twPageHeaderClose
import utils.horizontalSwipeListener
import webcomponents.BigButtonOption
import webcomponents.baseLayout
import webcomponents.genericBigButtonSwitch
import webcomponents.twContentScrollBox

enum class MapLayerTool { Style, Layer, SharedLayer }

object MapLayerToolSelectorStore : RootStore<MapLayerTool>(
    initialData = MapLayerTool.Layer,
    job = Job(),
) {

    val selectTool = handle<MapLayerTool> { _, tool ->
        update(tool)
        tool
    }

    val nextTool = handle { current ->
        if (current.ordinal < MapLayerTool.entries.size - 1) {
            MapLayerTool.entries[current.ordinal + 1]
        } else current
    }

    val previousTool = handle { current ->
        if (current.ordinal > 0) {
            MapLayerTool.entries[current.ordinal - 1]
        } else current
    }

    val reset = handle {
        update(MapLayerTool.Layer)
        MapLayerTool.Layer
    }
}

fun RenderContext.cardMapLayerSelection() {
    val translation: Translation by koinCtx.inject()
    val apiUserStore: ApiUserStore by koinCtx.inject()
    val mapLayerMetaDataListStore: MapLayerMetadataListStore by koinCtx.inject()
    val canAdd = apiUserStore.current.isInternalAdminGroupAdminOrGroupOwner()

    baseLayout(
        header = {
            twPageHeaderClose(
                backButtonTitle = TL.General.CLOSE,
                backButtonToolTipPlacement = PlacementValues.bottomEnd,
            ) {
                val options = mutableListOf(
                    BigButtonOption(
                        title = translation[TL.CardMapLayerSelection.MAP_LAYERS],
                        value = MapLayerTool.Layer,
                        selectHandler = MapLayerToolSelectorStore.selectTool,
                    ),
                )
                // FIXME this does not re render if the group changes
                Features.EnableUsingWorkspaceMapLayers.doIfFeatureFlagEnabled {
                    options.add(
                        BigButtonOption(
                            title = translation[TL.CardMapLayerSelection.SHARED_LAYERS],
                            value = MapLayerTool.SharedLayer,
                            selectHandler = MapLayerToolSelectorStore.selectTool,
                        ),
                    )
                }
                options.add(
                    BigButtonOption(
                        title = translation[TL.CardMapLayerSelection.MAP_STYLES],
                        value = MapLayerTool.Style,
                        selectHandler = MapLayerToolSelectorStore.selectTool,
                    ),
                )
                genericBigButtonSwitch(
                    MapLayerToolSelectorStore,
                    options = options,
                )
            }
        },
        content = {
            MapLayerToolSelectorStore.data.render { tool ->
                twContentScrollBox {
                    when (tool) {
                        MapLayerTool.Style -> {
                            twColOf {
                                val mapStyleEntries = if (OFFLINE_MODE) {
                                    MapStyleOffline.entries
                                } else {
                                    MapStyleOnline.entries
                                }
                                // calculate number of rows matching the given number of columns
                                val columns = 3
                                val rest = mapStyleEntries.size % columns
                                val rows = mapStyleEntries.size / columns + (if (rest > 0) 1 else 0)
                                for (i in 0 until rows) {
                                    mapStyleGridRow(
                                        items = mapStyleEntries
                                            .filter { it.ordinal in i * columns until i * columns + columns }
                                            .sortedBy { it.ordinal },
                                        columns = columns,
                                        itemSize = { "90px" },
                                        deviceIconSize = { normal },
                                        fontSize = { smaller },
                                    )
                                }
                            }
                        }

                        MapLayerTool.Layer -> {
                            twColOfCenter("py-2") {
                                mapLayerList(mapLayerMetaDataListStore.data)
                                if (canAdd) {
                                    addKeyWordLayerButton()
                                }
                            }
                        }

                        MapLayerTool.SharedLayer -> {
                            sharedLayerPicker()
                        }
                    }

                    horizontalSwipeListener(
                        domNode,
                        onSwipedRight = { MapLayerToolSelectorStore.nextTool() },
                        onSwipedLeft = { MapLayerToolSelectorStore.previousTool() },
                    )
                }
            }
        },
    )
}
